import type { BeatLineViteConfig } from '../api/types';

function qs<T extends Element = Element>(selector: string): T | null {
  return document.querySelector(selector) as T | null;
}

function qsa<T extends Element = Element>(selector: string): T[] {
  return Array.from(document.querySelectorAll(selector)) as T[];
}

function selectedRadioValue(name: string, fallback = ''): string {
  return qs<HTMLInputElement>(`input[name="${name}"]:checked`)?.value || fallback;
}

function intValue(value: unknown, fallback: number): number {
  const n = parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) ? n : fallback;
}

function activeStartTab(): string {
  return qs<HTMLElement>('#start-tabs .start-tab.active')?.dataset?.tab || 'local';
}

function setDisplay(el: HTMLElement | null, show: boolean, mode = 'block'): void {
  if (!el) return;
  el.style.display = show ? mode : 'none';
  el.toggleAttribute('hidden', !show);
  el.setAttribute('aria-hidden', show ? 'false' : 'true');
}

function syncWinSliderValue(): void {
  const slider = qs<HTMLInputElement>('#local-win-cards');
  const valueEl = qs<HTMLElement>('#local-win-cards-val');
  if (!slider || !valueEl) return;
  valueEl.textContent = String(intValue(slider.value, 10));
}

function syncSoloAndLocalControls(): void {
  const pCount = intValue(selectedRadioValue('p_count', '2'), 2);
  const soloMode = selectedRadioValue('solo_mode', 'classic');

  setDisplay(qs<HTMLElement>('#solo-settings'), pCount === 1);
  setDisplay(qs<HTMLElement>('#local-win-settings'), pCount > 1);
  setDisplay(qs<HTMLElement>('#timeattack-settings'), pCount === 1 && soloMode === 'timeattack');
  setDisplay(qs<HTMLElement>('#speedrun-settings'), pCount === 1 && soloMode === 'speedrun');

  document.dispatchEvent(new CustomEvent('beatline_mode_changed', {
    detail: { pCount, soloMode, activeTab: activeStartTab() }
  }));
}

function syncQrControls(config: BeatLineViteConfig): void {
  const wrap = qs<HTMLElement>('#qr-mode-wrap');
  const chk = qs<HTMLInputElement>('#qr-mode-enable');
  if (!wrap || !chk) return;

  const backendAllowsQr = Number(config.beatlineSettings?.showQrMode ?? 1) === 1;
  const pCount = intValue(selectedRadioValue('p_count', '2'), 2);
  const allowedBase = backendAllowsQr && activeStartTab() === 'local' && pCount >= 2 && pCount <= 4;

  setDisplay(wrap, allowedBase);
  chk.disabled = !allowedBase;
  if (!allowedBase) chk.checked = false;

  // Legacy game.js still performs the detailed Spotify-track availability check
  // after playlists are loaded. This TS controller owns only the basic backend/tab/player gating.
}

function syncAll(config: BeatLineViteConfig): void {
  syncWinSliderValue();
  syncSoloAndLocalControls();
  syncQrControls(config);
}

export function installStartScreenController(config: BeatLineViteConfig): void {
  const root = document.getElementById('beatline-app') || document.body;
  if (!root || root.dataset.beatlineTsStartController === '1') return;
  root.dataset.beatlineTsStartController = '1';

  const run = () => syncAll(config);

  qsa<HTMLInputElement>('input[name="p_count"], input[name="solo_mode"], input[name="timeattack_minutes"], input[name="speedrun_cards"], #qr-mode-enable').forEach((el) => {
    el.addEventListener('change', run);
  });

  qsa<HTMLElement>('#start-tabs .start-tab').forEach((tab) => {
    tab.addEventListener('click', () => window.setTimeout(run, 0));
  });

  qs<HTMLInputElement>('#local-win-cards')?.addEventListener('input', () => {
    syncWinSliderValue();
    run();
  });

  window.addEventListener('beatline_data_loaded', run);
  window.addEventListener('beatline_playlists_loaded', run as EventListener);
  document.addEventListener('DOMContentLoaded', run, { once: true });
  window.setTimeout(run, 0);
}
